/**
 * AVA-475 — axis lock.
 *
 * Ticking an axis declares: *"I am not measuring this one — take it from
 * design."* That constrains the pick. Lock one axis and it rides a plane (two
 * axes resolved from one view); lock two and it rides a line (one axis, one
 * click). It collapses the two-view capture for single-axis checks, because the
 * combine-views dance exists only to pin down a free 3D point.
 *
 * It is NOT AutoCAD's `.x` point filter. A point filter sources a coordinate
 * from a SECOND PICK; this sources it from DESIGN. The interaction feel is
 * borrowed, the coordinate-sourcing mechanism is not.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THREE RULES. Each prevents a specific way this feature can produce a report
 * that looks complete and is not. Do not relax them without reading why.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * RULE 1 — A LOCKED AXIS IS NEVER `PASS`.
 *   This is forced, not policy. Once a design value constrains which points are
 *   eligible, a found point cannot be evidence that the axis matches design:
 *   the search already excluded everything that would have disagreed. A locked
 *   axis can only ever produce "found" or "refused" — structurally never
 *   "measured". Rendering PASS would fabricate a verdict, and a fabricated pass
 *   on a conformance record is worse than a wrong number, because nothing about
 *   it looks wrong.
 *
 * RULE 2 — LOCKED VALUES NEVER ENTER THE DRAFT'S MEASURED AXES.
 *   `sharedAxisDisagreements` (asBuiltDraft.js) assumes every filled axis came
 *   from a real plane contribution. A locked value written the normal way
 *   becomes eligible for the agreement gate, which would then compare a genuine
 *   pick against an assumption and report it as two measurements agreeing. Locks
 *   live here, as a separate annotation, and are unioned in only at commit.
 *
 * RULE 3 — YOU CANNOT LOCK THE CURRENT VIEW'S DEPTH AXIS.
 *   The motivating example ("top-of-steel at a known plan position": lock X,Y,
 *   read Z in plan) is the single most dangerous configuration, because plan
 *   view's depth axis IS Z. It would take the one free axis from the one
 *   projection that cannot measure it, with the agreement gate structurally
 *   disabled. Measured live: depth from a single view is ~2 mm stable on a flat
 *   face and jumped 656 mm across 2 px on a silhouette edge. Locking an axis
 *   that is not the view's depth axis (lock Z, read X/Y in plan — both natively
 *   in-plane, 4 mm scatter) is safe and is what this permits.
 */
/** Axes that can carry a lock, in report order. */
export const LOCKABLE_AXES = Object.freeze(['x', 'y', 'z']);

/**
 * The axis a slice plane CANNOT measure — its depth/normal direction. Picking
 * resolves the two in-plane axes precisely and the third only as far as the ray
 * happens to land, which is why RULE 3 refuses to lock it.
 */
export const PLANE_DEPTH_AXIS = Object.freeze({ XY: 'z', XZ: 'y', YZ: 'x' });

/** No axis locked. `null` on an axis means "measure it". */
export function emptyLocks() {
  return { x: null, y: null, z: null };
}

/** True when the axis carries a lock. */
export function isLocked(locks, axis) {
  return Number.isFinite(locks?.[axis]);
}

/** The locked axes, in report order. */
export function lockedAxes(locks) {
  return LOCKABLE_AXES.filter((axis) => isLocked(locks, axis));
}

/** How many axes remain to be measured. */
export function freeAxes(locks) {
  return LOCKABLE_AXES.filter((axis) => !isLocked(locks, axis));
}

/**
 * Whether `axis` may be locked while `plane` is the active slice.
 *
 * RULE 3. Returns a reason string rather than a bare false so the UI can say
 * WHY a tick is disabled — a control that silently refuses reads as broken.
 *
 * @returns {{allowed: boolean, reason: string}}
 */
export function canLock(axis, plane, designPoint) {
  if (!LOCKABLE_AXES.includes(axis)) {
    return { allowed: false, reason: `${axis} is not a lockable axis` };
  }
  if (!designPoint || !Number.isFinite(designPoint[axis])) {
    return { allowed: false, reason: 'no design value to lock to' };
  }
  if (plane && PLANE_DEPTH_AXIS[plane] === axis) {
    return {
      allowed: false,
      reason: `${axis.toUpperCase()} is this view's depth axis — lock it and the free axis is read from the one projection that cannot measure it`,
    };
  }
  // Locking every axis would leave nothing to measure: the "capture" would
  // return the design point unchanged and report itself as an as-built.
  return { allowed: true, reason: '' };
}

/**
 * Locks `axis` to its design value. Returns a NEW object — Vue 2 cannot observe
 * a key added to an existing one, and the rest of this feature replaces draft
 * state wholesale for the same reason.
 */
export function lockAxis(locks, axis, designPoint, plane) {
  const verdict = canLock(axis, plane, designPoint);
  if (!verdict.allowed) return { ...locks };
  return { ...locks, [axis]: designPoint[axis] };
}

export function unlockAxis(locks, axis) {
  return { ...locks, [axis]: null };
}

export function unlockAll() {
  return emptyLocks();
}

/**
 * THE CORRIDOR.
 *
 * A locked axis cannot demand an exact coordinate match — a cloud with 1–2 mm
 * point spacing will never contain one. So a pick is accepted when it lands
 * within a corridor around the locked value, and refused outside it.
 *
 * The corridor IS the axis tolerance, deliberately:
 *   - inside it, treating the axis as nominal is defensible, because a genuine
 *     measurement landing there would have passed anyway;
 *   - outside it, the lock would hide the very thing worth reporting.
 * So the refusal doubles as a finding — "either this is a different feature, or
 * that axis really does deviate".
 *
 * NOT the 3:1 margin `TOLERANCE_PIXEL_RATIO` uses. That ratio governs whether
 * the SCREEN SCALE can resolve a tolerance; this governs whether a pick is on
 * the declared feature. Different question, different threshold.
 *
 * @param {object} picked Picked point, VIEWER units.
 * @param {object} locks Lock state, VIEWER units (sourced from design points).
 * @param {object} tolerance Per-axis tolerance in MILLIMETRES.
 * @param {number|null} mmPerViewerUnit From the caller's `unitTransform`. The
 *   corridor compares a viewer-space gap against a millimetre tolerance, so
 *   this is what makes the two comparable. Passed in, never resolved here —
 *   this module is pure and node-testable, and see utils/modelUnits.js for why
 *   a live viewer read would be wrong even if it could.
 * @returns {{ok: boolean, violations: Array}}
 */
export function checkAgainstLocks(picked, locks, tolerance, mmPerViewerUnit) {
  const violations = [];
  if (!picked) return { ok: true, violations };
  // Without a scale every deltaMm would be NaN, `NaN > tolerance` is false, and
  // the corridor would silently accept every pick — a gate that reports "ok" is
  // worse than one that refuses. Unreachable in practice: capture is gated on
  // the transform upstream (SliceController refuses to slice without one).
  if (!Number.isFinite(mmPerViewerUnit) || mmPerViewerUnit <= 0) {
    return { ok: true, violations };
  }

  // Absorbs binary floating-point representation error so the INCLUSIVE
  // boundary really is inclusive. The delta is a viewer-space gap scaled to
  // millimetres, so a pick exactly at tolerance lands on values like
  // 3.0000000000001137 and would be refused for no physical reason. 1e-6 mm is
  // a nanometre — far below anything this feature can measure, resolve or
  // report, so it cannot mask a real violation.
  const FLOAT_EPSILON_MM = 1e-6;

  for (const axis of lockedAxes(locks)) {
    const pickedValue = picked[axis];
    if (!Number.isFinite(pickedValue)) continue;

    const lockedValue = locks[axis];
    const deltaMm = Math.abs(pickedValue - lockedValue) * mmPerViewerUnit;
    const toleranceMm = tolerance?.[axis];
    if (!Number.isFinite(toleranceMm)) continue;

    if (deltaMm - toleranceMm > FLOAT_EPSILON_MM) {
      violations.push({ axis, locked: lockedValue, picked: pickedValue, deltaMm, toleranceMm });
    }
  }
  return { ok: violations.length === 0, violations };
}

/**
 * Unions the locked axes into a committed as-built point.
 *
 * RULE 2's other half: this is the ONLY place a locked value becomes part of a
 * point, and it happens at commit — after the agreement gate has run on the
 * measured axes alone.
 *
 * @param {object} measured The point assembled from real picks, VIEWER units.
 * @param {object} locks Lock state, VIEWER units — locked values are copied
 *   straight from the design point, so they are already in its space.
 * @returns {object} A new point carrying both.
 */
export function applyLocks(measured, locks) {
  const out = { ...(measured || {}) };
  for (const axis of lockedAxes(locks)) out[axis] = locks[axis];
  return out;
}

/**
 * How the capture should be described once locks are in play — e.g.
 * "Z check · X, Y locked". RULE 1 made visible: a 3-axis-looking result that is
 * honestly a 1-axis check must SAY so, as prominently as the verdict.
 *
 * Shown wherever a verdict is shown, never as a footnote: the realistic failure
 * is not a wrong number (re-survey catches that) but a report that looks like a
 * completed 3-axis verification and is a 1-axis check, because nobody
 * re-surveys what looks complete.
 */
export function describeLockScope(locks) {
  const locked = lockedAxes(locks);
  if (!locked.length) return '';
  const free = freeAxes(locks);
  const upper = (list) => list.map((a) => a.toUpperCase()).join(', ');
  if (!free.length) return 'Nothing measured — all axes locked';
  return `${upper(free)} check · ${upper(locked)} locked`;
}

/**
 * RULE 1, enforced on the derived verdict.
 *
 * Strips the pass flag off every locked axis and recomputes the point's overall
 * verdict from the MEASURED axes alone. A locked axis reports `null` — the same
 * value an unmeasured axis reports — because that is exactly what it is.
 *
 * The overall verdict of a point whose every axis is locked is `null`, never
 * `true`: nothing was measured, so nothing passed.
 *
 * @param {object} verdict Output of `evaluatePoint`.
 * @param {string[]} locked Axis keys asserted from design.
 * @returns {object} A new verdict with locked axes neutralised.
 */
export function applyLockRule(verdict, locked) {
  if (!verdict || !locked?.length) return verdict;

  const out = { ...verdict };
  const flagOf = { x: 'passX', y: 'passY', z: 'passZ' };
  for (const axis of locked) out[flagOf[axis]] = null;

  const measured = LOCKABLE_AXES.filter((axis) => !locked.includes(axis));
  if (!measured.length) {
    out.pass = null;
    return out;
  }
  const flags = measured.map((axis) => out[flagOf[axis]]);
  out.pass = flags.some((f) => f === null) ? null : flags.every(Boolean);
  return out;
}

/** "1 of 3 axes independently measured" — the completeness statement. */
export function describeMeasuredCount(locks) {
  return `${freeAxes(locks).length} of ${LOCKABLE_AXES.length} axes independently measured`;
}
